// OP-3605 — Inventários (ESL1 para ver; abrir com ESL7): a contagem física da
// praça. Uma contagem aberta por praça de cada vez — abrir a segunda é o que
// `podeAbrirInventario` recusa.
import { FC, useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueInventarios, useEstoqueLocais } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { createEstoqueInventario } from '../../../../Service/estoque'
import { InventarioRow, InventariosFilters, StatusInventario } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, CaixaLista, Card, Chip, Field, FiltersRow, ListaCartoes, PageSubtitle,
  PageTitle, Select, SoDesktop, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { Cartao } from '../Cartao'
import { tratarErro } from '../erros'
import { statusDaUrl } from '../filtrosUrl'
import { plural } from '../formato'
import { STATUS_INVENTARIO_UI, pracaComInventarioAberto } from '../Inventario/regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const STATUS: StatusInventario[] = ['EM_CONTAGEM', 'AGUARDANDO_APROVACAO', 'FECHADO', 'DESCARTADO']
const COLS = 6

export const EstoqueInventarios: FC = () => {
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeInventariar = pode('INVENTARIAR')
  const [searchParams] = useSearchParams()
  const statusUrl = statusDaUrl(searchParams, STATUS)
  const [draft, setDraft] = useState<InventariosFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const [applied, setApplied] = useState<InventariosFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const { rows, total, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueInventarios(applied)

  useEffect(() => {
    if (!statusUrl) return
    setDraft((d) => ({ ...d, status: statusUrl, page: 1 }))
    setApplied((a) => ({ ...a, status: statusUrl, page: 1 }))
  }, [statusUrl])
  const { locais } = useEstoqueLocais()
  const [abrirEm, setAbrirEm] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const pagina = Math.max(1, Number(applied.page) || 1)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  // O aviso olha a página carregada: a régua de verdade é do servidor, que
  // recusa a segunda contagem com INVENTARIO_JA_ABERTO.
  const abertoNaPraca = abrirEm != null ? pracaComInventarioAberto(rows, abrirEm) : null

  const abrir = async () => {
    if (abrirEm == null) return
    setBusy(true)
    try {
      const { data } = await createEstoqueInventario({ localId: abrirEm })
      toast.success(`Inventário ${data?.data?.numero ?? ''} aberto`)
      navigate(`/interno/estoque/inventarios/${data?.data?.id}`)
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const escolherStatus = (status: StatusInventario | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }

  const cartao = (i: InventarioRow) => {
    const ui = STATUS_INVENTARIO_UI[i.status] ?? { bg: '#F3F4F6', color: '#374151', label: i.status }
    const esperaAprovacao = i.status === 'AGUARDANDO_APROVACAO'
    return (
      <Cartao
        key={i.id}
        to={`/interno/estoque/inventarios/${i.id}`}
        codigo={i.numero}
        badge={ui}
        titulo={i.localNome}
        esmaecido={i.status === 'FECHADO' || i.status === 'DESCARTADO'}
        destaque={esperaAprovacao}
        meta={(
          <>
            aberta {fmtDate(i.abertoEm)} por {i.contadoPorNome}
            {i.fechadoEm ? <div>fechada {fmtDate(i.fechadoEm)}</div> : null}
          </>
        )}
        rodape={(() => {
          if (esperaAprovacao) return <span style={{ color: BD.redDark, fontWeight: 600 }}>Quem contou não aprova: espera a coordenação</span>
          if (i.status === 'EM_CONTAGEM') return <span style={{ color: BD.grayText }}>contagem em andamento</span>
          return (
            <span style={{ color: BD.grayText }}>
              {i.aprovadoPorNome ? `ajuste aprovado por ${i.aprovadoPorNome}` : 'fechada sem divergência'}
            </span>
          )
        })()}
      />
    )
  }

  const linha = (i: InventarioRow) => {
    const ui = STATUS_INVENTARIO_UI[i.status] ?? { bg: '#F3F4F6', color: '#374151', label: i.status }
    return (
      <tr key={i.id} onClick={() => navigate(`/interno/estoque/inventarios/${i.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{i.numero}</td>
        <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
        <td>{i.localNome}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(i.abertoEm)}<div style={{ fontSize: 11, color: BD.grayText }}>por {i.contadoPorNome}</div></td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(i.fechadoEm)}</td>
        <td style={{ fontSize: 13 }}>{i.aprovadoPorNome ?? '—'}{i.motivoAjuste && <div style={{ fontSize: 11, color: BD.grayText }}>{i.motivoAjuste}</div>}</td>
      </tr>
    )
  }

  return (
    <>
      <PageTitle>Inventário</PageTitle>
      <PageSubtitle>
        A contagem física de uma praça. O esperado é fotografado na abertura: se alguém movimentar a praça no meio, a aprovação recusa e a contagem precisa ser refeita.
      </PageSubtitle>

      {podeInventariar && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Abrir contagem</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field>
              <label>Praça</label>
              <Select value={abrirEm ?? ''} onChange={(e) => setAbrirEm(Number(e.target.value) || null)}>
                <option value="">— escolha —</option>
                {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </Select>
            </Field>
            <Button disabled={busy || abrirEm == null || !!abertoNaPraca} onClick={abrir}>Abrir contagem</Button>
            {abertoNaPraca && (
              <span style={{ fontSize: 13, color: BD.grayText }}>
                Esta praça já tem a contagem {abertoNaPraca.numero} em andamento.{' '}
                <a href={`/interno/estoque/inventarios/${abertoNaPraca.id}`} onClick={(e) => { e.preventDefault(); navigate(`/interno/estoque/inventarios/${abertoNaPraca.id}`) }}>abrir</a>
              </span>
            )}
          </div>
        </Card>
      )}

      <BarraChips role="group" aria-label="Status da contagem">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>Todas</Chip>
        {STATUS.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_INVENTARIO_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <SoDesktop>
        <FiltersRow>
          <Field>
            <label>Status</label>
            <Select value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value as StatusInventario | '' })}>
              <option value="">todos</option>
              {STATUS.map((s) => <option key={s} value={s}>{STATUS_INVENTARIO_UI[s].label}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => setApplied({ ...draft, page: 1 })}>Aplicar</Button></div>
        </FiltersRow>
      </SoDesktop>

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : plural(total, 'contagem', 'contagens')}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Número</th><th>Status</th><th>Praça</th><th>Aberta</th><th>Fechada</th><th>Aprovação</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar os inventários. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma contagem com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar os inventários. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma contagem com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={pagina <= 1} onClick={() => setApplied((a) => ({ ...a, page: pagina - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagina}</span>
          <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setApplied((a) => ({ ...a, page: pagina + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>
    </>
  )
}

export default EstoqueInventarios
